<template>
  <div class="erp-transfer-page">
    <card :title="$t('tools.erp_integration.transfer_title')" :hide-title="true" id="erp-transfer">
      <div class="transfer-header">
        <h1 class="transfer-header__title">{{ $t("tools.erp_integration.transfer_title") }}</h1>
        <div class="transfer-header__actions">
          <router-link
            to="/tools/erp-integration"
            class="btn btn-md btn-secondary transfer-header__icon-button"
            :aria-label="$t('common.back_to_list')"
            :title="$t('common.back_to_list')"
          >
            <span class="dx-icon dx-icon-arrowleft" aria-hidden="true"></span>
          </router-link>
          <button
            type="button"
            class="btn btn-md btn-secondary transfer-header__icon-button"
            :disabled="loading"
            :aria-label="$t('common.refresh')"
            :title="$t('common.refresh')"
            @click="bootstrap"
          >
            <span class="dx-icon dx-icon-refresh" aria-hidden="true"></span>
          </button>
        </div>
      </div>

      <div class="view-switcher" role="tablist">
        <button
          type="button"
          class="view-switcher__btn"
          :class="{ active: activeView === 'setup' }"
          role="tab"
          :aria-selected="activeView === 'setup'"
          @click="activeView = 'setup'"
        >
          {{ $t("tools.erp_integration.transfer_view_setup") }}
        </button>
        <button
          type="button"
          class="view-switcher__btn"
          :class="{ active: activeView === 'mappings' }"
          role="tab"
          :aria-selected="activeView === 'mappings'"
          @click="activeView = 'mappings'"
        >
          {{ $t("tools.erp_integration.transfer_view_mappings") }}
        </button>
        <button
          type="button"
          class="view-switcher__btn"
          :class="{ active: activeView === 'queue' }"
          role="tab"
          :aria-selected="activeView === 'queue'"
          @click="activeView = 'queue'"
        >
          {{ $t("tools.erp_integration.transfer_view_queue") }}
        </button>
        <button
          type="button"
          class="view-switcher__btn"
          :class="{ active: activeView === 'audit' }"
          role="tab"
          :aria-selected="activeView === 'audit'"
          @click="activeView = 'audit'"
        >
          {{ $t("tools.erp_integration.transfer_view_audit") }}
        </button>
        <button
          type="button"
          class="view-switcher__btn"
          :class="{ active: activeView === 'preview' }"
          role="tab"
          :aria-selected="activeView === 'preview'"
          @click="activeView = 'preview'"
        >
          {{ $t("tools.erp_integration.transfer_view_preview") }}
        </button>
      </div>

      <!-- SETUP VIEW -->
      <div v-if="activeView === 'setup'" class="view-panel">
        <div class="checkbox-row checkbox-row--master">
          <label>
            <input v-model="settingsForm.transfer_integration_enabled" type="checkbox" />
            {{ $t("tools.erp_integration.transfer_integration_enabled") }}
          </label>
          <small>{{ $t("tools.erp_integration.transfer_integration_enabled_hint") }}</small>
        </div>

        <fieldset class="settings-grid" :class="{ 'settings-grid--disabled': !settingsForm.transfer_integration_enabled }" :disabled="!settingsForm.transfer_integration_enabled">
          <div class="form-group">
            <label>{{ $t("tools.erp_integration.transfer_iesiri_endpoint") }}</label>
            <input v-model="settingsForm.iesiri_endpoint" type="text" class="form-control" />
            <small class="text-muted">{{ $t("tools.erp_integration.transfer_iesiri_endpoint_hint") }}</small>
          </div>

          <div class="form-group">
            <label>{{ $t("tools.erp_integration.transfer_intrari_endpoint") }}</label>
            <input v-model="settingsForm.intrari_endpoint" type="text" class="form-control" />
            <small class="text-muted">{{ $t("tools.erp_integration.transfer_intrari_endpoint_hint") }}</small>
          </div>

          <div class="form-group">
            <label>{{ $t("tools.erp_integration.transfer_warehouse_endpoint") }}</label>
            <input v-model="settingsForm.warehouse_transfer_endpoint" type="text" class="form-control" />
            <small class="text-muted">{{ $t("tools.erp_integration.transfer_warehouse_endpoint_hint") }}</small>
          </div>

          <div class="form-group">
            <label>{{ $t("tools.erp_integration.transfer_default_carnet_iesiri") }}</label>
            <input v-model="settingsForm.default_carnet_iesiri" type="text" class="form-control" />
          </div>

          <div class="form-group">
            <label>{{ $t("tools.erp_integration.transfer_default_carnet_nir") }}</label>
            <input v-model="settingsForm.default_carnet_nir" type="text" class="form-control" />
          </div>

          <div class="form-group">
            <label>{{ $t("tools.erp_integration.transfer_default_carnet_transfer") }}</label>
            <input v-model="settingsForm.default_carnet_transfer" type="text" class="form-control" />
            <small class="text-muted">{{ $t("tools.erp_integration.transfer_default_carnet_transfer_hint") }}</small>
          </div>

          <div class="form-group">
            <label>{{ $t("tools.erp_integration.transfer_default_carnet_transfer_nir") }}</label>
            <input v-model="settingsForm.default_carnet_transfer_nir" type="text" class="form-control" />
            <small class="text-muted">{{ $t("tools.erp_integration.transfer_default_carnet_transfer_nir_hint") }}</small>
          </div>

          <div class="form-group">
            <label>{{ $t("tools.erp_integration.transfer_default_carnet_transfer_livr") }}</label>
            <input v-model="settingsForm.default_carnet_transfer_livr" type="text" class="form-control" />
            <small class="text-muted">{{ $t("tools.erp_integration.transfer_default_carnet_transfer_livr_hint") }}</small>
          </div>

          <div class="form-group">
            <label>{{ $t("tools.erp_integration.transfer_warehouse_next_nr_doc") }}</label>
            <input v-model.number="settingsForm.warehouse_next_nr_doc" type="number" min="1" class="form-control" />
            <small class="text-muted">{{ $t("tools.erp_integration.transfer_warehouse_next_nr_doc_hint") }}</small>
          </div>

          <div class="form-group">
            <label>{{ $t("tools.erp_integration.transfer_retry_max_attempts") }}</label>
            <input v-model.number="settingsForm.retry_max_attempts" type="number" min="1" max="100" class="form-control" />
          </div>

          <div class="form-group">
            <label>{{ $t("tools.erp_integration.transfer_retry_backoff") }}</label>
            <input v-model.number="settingsForm.retry_backoff_base_seconds" type="number" min="10" max="3600" class="form-control" />
          </div>

          <div class="form-group">
            <label>{{ $t("tools.erp_integration.transfer_batch_size") }}</label>
            <input v-model.number="settingsForm.batch_size" type="number" min="1" max="1000" class="form-control" />
          </div>

          <div class="form-group">
            <label>{{ $t("tools.erp_integration.transfer_timeout_seconds") }}</label>
            <input v-model.number="settingsForm.timeout_seconds" type="number" min="30" max="600" class="form-control" />
          </div>

          <div class="checkbox-row col-span-2">
            <label>
              <input v-model="settingsForm.enable_auto_send" type="checkbox" />
              {{ $t("tools.erp_integration.transfer_enable_auto_send") }}
            </label>
            <label>
              <input v-model="settingsForm.enable_sm_source" type="checkbox" />
              {{ $t("tools.erp_integration.transfer_enable_sm_source") }}
            </label>
            <label>
              <input v-model="settingsForm.enable_warehouse_source" type="checkbox" />
              {{ $t("tools.erp_integration.transfer_enable_warehouse_source") }}
            </label>
          </div>
        </fieldset>

        <div class="settings-actions">
          <button type="button" class="btn btn-primary" :disabled="savingSettings" @click="saveSettings">
            {{ $t("common.save") }}
          </button>
        </div>
      </div>

      <!-- MAPPINGS VIEW -->
      <div v-if="activeView === 'mappings'" class="view-panel">
        <div class="panel-toolbar">
          <button type="button" class="btn btn-sm btn-outline-primary" @click="openMappingPopup()">
            {{ $t("common.add") }}
          </button>
          <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="loadingMappings" @click="loadMappings">
            {{ $t("common.refresh") }}
          </button>
        </div>

        <DxDataGrid
          :data-source="mappings"
          key-expr="id"
          :show-borders="true"
          :row-alternation-enabled="true"
          :column-auto-width="true"
          :no-data-text="$t('common.no_data_text')"
        >
          <DxColumn data-field="mapping_type" :caption="$t('tools.erp_integration.transfer_mapping_type')" />
          <DxColumn data-field="source_kind" :caption="$t('tools.erp_integration.transfer_source_kind')" />
          <DxColumn data-field="internal_key" :caption="$t('tools.erp_integration.transfer_internal_key')" />
          <DxColumn data-field="internal_label" :caption="$t('tools.erp_integration.transfer_internal_label')" />
          <DxColumn data-field="wm_key" :caption="$t('tools.erp_integration.transfer_wm_key')" />
          <DxColumn data-field="static_value" :caption="$t('tools.erp_integration.transfer_static_value')" />
          <DxColumn data-field="priority" :caption="$t('tools.erp_integration.transfer_priority')" width="90" />
          <DxColumn data-field="is_active" :caption="$t('tools.erp_integration.transfer_active')" width="90" />
          <DxColumn type="buttons" width="110">
            <DxButton icon="edit" :hint="$t('common.edit')" :on-click="onEditMapping" />
            <DxButton icon="trash" :hint="$t('common.delete')" :on-click="onDeleteMapping" />
          </DxColumn>
        </DxDataGrid>

        <DxPopup
          :visible.sync="mappingPopupVisible"
          :title="editingMappingId ? $t('common.edit') : $t('common.add')"
          :show-close-button="true"
          :width="700"
          :height="540"
        >
          <div class="mapping-form">
            <div class="form-group">
              <label>{{ $t("tools.erp_integration.transfer_mapping_type") }}</label>
              <select v-model="mappingForm.mapping_type" class="form-control">
                <option v-for="item in mappingTypeOptions" :key="item" :value="item">{{ item }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>{{ $t("tools.erp_integration.transfer_source_kind") }}</label>
              <select v-model="mappingForm.source_kind" class="form-control">
                <option value="INTERNAL_UUID">INTERNAL_UUID</option>
                <option value="STATIC_VALUE">STATIC_VALUE</option>
                <option value="ERP_ENTITY_RECORD">ERP_ENTITY_RECORD</option>
              </select>
            </div>
            <div class="form-group">
              <label>{{ $t("tools.erp_integration.transfer_internal_key") }}</label>
              <input v-model="mappingForm.internal_key" type="text" class="form-control" />
            </div>
            <div class="form-group">
              <label>{{ $t("tools.erp_integration.transfer_internal_label") }}</label>
              <input v-model="mappingForm.internal_label" type="text" class="form-control" />
            </div>
            <div class="form-group">
              <label>{{ $t("tools.erp_integration.transfer_wm_key") }}</label>
              <input v-model="mappingForm.wm_key" type="text" class="form-control" />
            </div>
            <div class="form-group">
              <label>{{ $t("tools.erp_integration.transfer_static_value") }}</label>
              <input v-model="mappingForm.static_value" type="text" class="form-control" />
            </div>
            <div class="form-group">
              <label>{{ $t("tools.erp_integration.transfer_priority") }}</label>
              <input v-model.number="mappingForm.priority" type="number" min="0" max="9999" class="form-control" />
            </div>
            <div class="checkbox-row">
              <label><input v-model="mappingForm.is_active" type="checkbox" /> {{ $t("tools.erp_integration.transfer_active") }}</label>
              <label><input v-model="mappingForm.is_fallback" type="checkbox" /> {{ $t("tools.erp_integration.transfer_fallback") }}</label>
            </div>
            <div class="settings-actions">
              <button type="button" class="btn btn-primary" :disabled="savingMapping" @click="saveMapping">
                {{ $t("common.save") }}
              </button>
            </div>
          </div>
        </DxPopup>
      </div>

      <!-- QUEUE VIEW -->
      <div v-if="activeView === 'queue'" class="view-panel">
        <div class="queue-filters">
          <input
            v-model="queueFilters.search"
            type="text"
            class="form-control"
            :placeholder="$t('tools.erp_integration.transfer_search_placeholder')"
          />
          <select v-model="queueFilters.status" class="form-control">
            <option value="">{{ $t("common.all") }}</option>
            <option v-for="item in queueStatusOptions" :key="item" :value="item">{{ item }}</option>
          </select>
          <button type="button" class="btn btn-sm btn-outline-primary" :disabled="loadingQueue" @click="loadQueue">
            {{ $t("common.search") }}
          </button>
          <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="loadingQueue" @click="runBulkRetry">
            {{ $t("tools.erp_integration.transfer_bulk_retry") }}
          </button>
        </div>

        <DxDataGrid
          :data-source="queue"
          key-expr="uuid"
          :show-borders="true"
          :row-alternation-enabled="true"
          :column-auto-width="true"
          :no-data-text="$t('common.no_data_text')"
          @selection-changed="onQueueSelectionChanged"
          @row-expanding="onQueueRowExpanding"
        >
          <DxSelection mode="single" />
          <DxMasterDetail :enabled="true" template="queueAuditDetail" />
          <DxColumn data-field="updated_at" :caption="$t('common.last_update')" data-type="datetime" :sort-order="'desc'" />
          <DxColumn data-field="created_at" :caption="$t('common.created_at')" data-type="datetime" />
          <DxColumn data-field="status" :caption="$t('common.status')" cell-template="statusCell" />
          <template #statusCell="{ data }">
            <span class="badge" :class="getStatusBadgeClass(data.data.status)">
              {{ data.data.status }}
            </span>
          </template>
          <DxColumn data-field="source_document_number" :caption="$t('tools.erp_integration.transfer_document_number')" cell-template="documentNumberCell" />
          <template #documentNumberCell="{ data }">
            {{ getTransferDocumentNumber(data.data) }}
          </template>
          <DxColumn data-field="wm_operation" :caption="$t('tools.erp_integration.transfer_operation')" />
          <DxColumn data-field="source_department_name" :caption="$t('tools.erp_integration.transfer_source_subunit')" cell-template="sourceSubunitCell" />
          <template #sourceSubunitCell="{ data }">
            {{ getTransferSubunit(data.data, "source") }}
          </template>
          <DxColumn data-field="dest_department_name" :caption="$t('tools.erp_integration.transfer_dest_subunit')" cell-template="destSubunitCell" />
          <template #destSubunitCell="{ data }">
            {{ getTransferSubunit(data.data, "dest") }}
          </template>
          <DxColumn data-field="source_warehouse_name" :caption="$t('tools.erp_integration.transfer_source_warehouse')" />
          <DxColumn data-field="dest_warehouse_name" :caption="$t('tools.erp_integration.transfer_dest_warehouse')" />
          <DxColumn data-field="wm_document_code_out" :caption="$t('tools.erp_integration.transfer_cod_ies')" />
          <DxColumn data-field="wm_document_code_transfer" :caption="$t('tools.erp_integration.transfer_cod_transfer')" />
          <DxColumn data-field="attempt_count" :caption="$t('tools.erp_integration.transfer_attempts')" width="80" />
          <DxColumn data-field="last_error_code" :caption="$t('tools.erp_integration.transfer_last_error')" cell-template="errorCell" />
          <template #errorCell="{ data }">
            <span :title="data.data.last_error_message || data.data.last_error_code" :class="{ 'text-danger': !!data.data.last_error_code }">
              <span v-if="data.data.last_error_step" class="badge badge-sm badge-outline-warning mr-1">[{{ data.data.last_error_step }}]</span>
              {{ data.data.last_error_code || '-' }}
            </span>
          </template>
          <DxColumn type="buttons" width="220">
            <DxButton icon="find" :hint="$t('tools.erp_integration.transfer_view_audit')" :on-click="onOpenAudit" />
            <DxButton icon="preview" :hint="$t('tools.erp_integration.transfer_preview')" :on-click="onPreviewQueueItem" />
            <DxButton icon="check" :hint="$t('tools.erp_integration.consum_validate')" :on-click="onValidateQueueItem" />
            <DxButton icon="export" :hint="$t('tools.erp_integration.consum_send')" :on-click="onSendQueueItem" />
            <DxButton icon="refresh" :hint="$t('tools.erp_integration.consum_retry')" :on-click="onRetryQueueItem" />
            <DxButton icon="close" :hint="$t('tools.erp_integration.consum_cancel')" :on-click="onCancelQueueItem" />
          </DxColumn>
          <template #queueAuditDetail="{ data }">
            <div class="queue-audit-detail">
              <div v-if="queueAuditLoading[data.data.uuid]" class="text-muted">
                {{ $t("common.please_wait") }}
              </div>
              <div v-else-if="queueAuditErrors[data.data.uuid]" class="alert alert-danger mb-2">
                <span>{{ queueAuditErrors[data.data.uuid] }}</span>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary ml-2"
                  @click="loadQueueAudit(data.data, true)"
                >
                  {{ $t("common.refresh") }}
                </button>
              </div>
              <DxDataGrid
                v-else
                :data-source="queueAuditByUuid[data.data.uuid]?.events || []"
                key-expr="id"
                :show-borders="true"
                :row-alternation-enabled="true"
                :column-auto-width="true"
                :word-wrap-enabled="true"
                :no-data-text="$t('common.no_data_text')"
              >
                <DxMasterDetail :enabled="true" template="queueAuditEventDetail" />
                <DxColumn data-field="created_at" :caption="$t('common.created_at')" data-type="datetime" width="160" />
                <DxColumn data-field="event_type" :caption="$t('tools.erp_integration.transfer_event_type')" width="140" />
                <DxColumn data-field="step" :caption="$t('tools.erp_integration.transfer_event_step')" width="100" />
                <DxColumn data-field="previous_status" :caption="$t('tools.erp_integration.transfer_previous_status')" width="120" />
                <DxColumn data-field="new_status" :caption="$t('tools.erp_integration.transfer_new_status')" width="120" />
                <DxColumn data-field="message" :caption="$t('common.message')" cell-template="queueAuditMessageCell" />
                <template #queueAuditMessageCell="{ data: event }">
                  <div class="audit-message-cell">
                    {{ event.data.message }}
                  </div>
                </template>
                <template #queueAuditEventDetail="{ data: event }">
                  <div class="preview-split audit-event-payload">
                    <div class="preview-col">
                      <h5>{{ $t("tools.erp_integration.transfer_sent_payload") }}</h5>
                      <pre class="json-viewer">{{ formatTransferAuditJson(event.data.request_payload_json) }}</pre>
                    </div>
                    <div class="preview-col">
                      <h5>{{ $t("tools.erp_integration.transfer_response_payload") }}</h5>
                      <pre class="json-viewer">{{ formatTransferAuditJson(event.data.response_payload_json) }}</pre>
                    </div>
                  </div>
                </template>
              </DxDataGrid>
            </div>
          </template>
        </DxDataGrid>
      </div>

      <!-- AUDIT VIEW -->
      <div v-if="activeView === 'audit'" class="view-panel">
        <div v-if="!selectedQueueItem" class="placeholder-panel">
          <h5>{{ $t("tools.erp_integration.transfer_audit_empty_title") }}</h5>
          <p>{{ $t("tools.erp_integration.transfer_audit_empty_description") }}</p>
        </div>

        <div v-else class="audit-wrap">
          <div class="audit-head">
            <div><strong>{{ $t("tools.erp_integration.transfer_document_number") }}:</strong> {{ getTransferDocumentNumber(selectedQueueItem) }}</div>
            <div><strong>{{ $t("tools.erp_integration.transfer_source_subunit") }}:</strong> {{ getTransferSubunit(selectedQueueItem, "source") }}</div>
            <div><strong>{{ $t("tools.erp_integration.transfer_dest_subunit") }}:</strong> {{ getTransferSubunit(selectedQueueItem, "dest") }}</div>
            <div><strong>{{ $t("tools.erp_integration.transfer_source_warehouse") }}:</strong> {{ selectedQueueItem.source_warehouse_name || '-' }}</div>
            <div><strong>{{ $t("tools.erp_integration.transfer_dest_warehouse") }}:</strong> {{ selectedQueueItem.dest_warehouse_name || '-' }}</div>
            <div><strong>{{ $t("tools.erp_integration.transfer_operation") }}:</strong> {{ selectedQueueItem.wm_operation || '-' }}</div>
            <div><strong>{{ $t("tools.erp_integration.transfer_cod_ies") }}:</strong> {{ selectedQueueItem.wm_document_code_out || '-' }}</div>
            <div><strong>{{ $t("tools.erp_integration.transfer_cod_transfer") }}:</strong> {{ selectedQueueItem.wm_document_code_transfer || '-' }}</div>
            <div><strong>{{ $t("common.status") }}:</strong> <span class="badge" :class="getStatusBadgeClass(selectedQueueItem.status)">{{ selectedQueueItem.status }}</span></div>
          </div>

          <div v-if="selectedQueueItem.last_error_message" class="alert alert-danger mt-2 mb-2">
            <strong>{{ selectedQueueItem.last_error_code || $t('error') }}:</strong>
            <div>{{ selectedQueueItem.last_error_message }}</div>
          </div>

          <DxDataGrid
            :data-source="selectedQueueItem.events || []"
            key-expr="id"
            :show-borders="true"
            :row-alternation-enabled="true"
            :column-auto-width="true"
            :word-wrap-enabled="true"
            :no-data-text="$t('common.no_data_text')"
          >
            <DxColumn data-field="created_at" :caption="$t('common.created_at')" data-type="datetime" width="160" />
            <DxColumn data-field="event_type" :caption="$t('tools.erp_integration.transfer_event_type')" width="140" />
            <DxColumn data-field="step" :caption="$t('tools.erp_integration.transfer_event_step')" width="100" />
            <DxColumn data-field="previous_status" :caption="$t('tools.erp_integration.transfer_previous_status')" width="120" />
            <DxColumn data-field="new_status" :caption="$t('tools.erp_integration.transfer_new_status')" width="120" />
            <DxColumn data-field="message" :caption="$t('common.message')" cell-template="auditMessageCell" />
            <template #auditMessageCell="{ data }">
              <div class="audit-message-cell">
                {{ data.data.message }}
              </div>
            </template>
          </DxDataGrid>
        </div>
      </div>

      <!-- PREVIEW VIEW -->
      <div v-if="activeView === 'preview'" class="view-panel">
        <div class="panel-toolbar">
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            :disabled="!selectedQueueItem"
            @click="loadPreview(selectedQueueItem)"
          >
            {{ $t("tools.erp_integration.transfer_refresh_preview") }}
          </button>
        </div>

        <div v-if="!selectedQueueItem && !preview" class="placeholder-panel">
          <h5>{{ $t("tools.erp_integration.transfer_preview_empty_title") }}</h5>
          <p>{{ $t("tools.erp_integration.transfer_preview_empty_description") }}</p>
        </div>

        <div v-if="preview" class="preview-wrap">
          <div class="preview-status" :class="{ 'preview-status--valid': preview.validation && preview.validation.valid, 'preview-status--invalid': preview.validation && !preview.validation.valid }">
            <div class="preview-status__title">
              {{ preview.validation && preview.validation.valid ? $t("tools.erp_integration.transfer_preview_valid") : $t("tools.erp_integration.transfer_preview_invalid") }}
            </div>
            <div v-if="preview.validation && preview.validation.summary" class="preview-status__summary">
              {{ preview.validation.summary }}
            </div>
          </div>

          <div class="preview-split">
            <div v-if="preview.payload && preview.payload.operation === 'WAREHOUSE_TRANSFER'" class="preview-col">
              <h5>{{ $t("tools.erp_integration.transfer_step_transfer") }} (POST /Transferuri)</h5>
              <div class="alert alert-warning" v-if="shouldShowWarehouseStockWarning">
                {{ $t("tools.erp_integration.transfer_stock_warning") }}
              </div>
              <pre class="json-viewer">{{ formatJson(warehouseTransferPayload) }}</pre>
            </div>
            <div v-else class="preview-col">
              <h5>{{ $t("tools.erp_integration.transfer_step1_iesiri") }} (POST /IesiriSubunit)</h5>
              <pre class="json-viewer">{{ formatJson(preview.payload ? preview.payload.iesiri : {}) }}</pre>
            </div>
            <div v-if="!preview.payload || preview.payload.operation !== 'WAREHOUSE_TRANSFER'" class="preview-col">
              <h5>{{ $t("tools.erp_integration.transfer_step2_intrari") }} (POST /IntraridelaSubunitati)</h5>
              <pre class="json-viewer">{{ formatJson(preview.payload ? preview.payload.intrari : {}) }}</pre>
            </div>
          </div>

          <div v-if="preview.mapping_resolutions && preview.mapping_resolutions.length" class="resolutions-box mt-3">
            <h5>{{ $t("tools.erp_integration.transfer_mapping_resolutions") }}</h5>
            <DxDataGrid
              :data-source="preview.mapping_resolutions"
              key-expr="mapping_type"
              :show-borders="true"
              :row-alternation-enabled="true"
              :column-auto-width="true"
            >
              <DxColumn data-field="mapping_type" :caption="$t('tools.erp_integration.transfer_mapping_type')" />
              <DxColumn data-field="internal_label" :caption="$t('tools.erp_integration.transfer_internal_label')" />
              <DxColumn data-field="value" :caption="$t('tools.erp_integration.transfer_resolved_value')" />
              <DxColumn data-field="matched" :caption="$t('tools.erp_integration.transfer_matched')" cell-template="matchedCell" />
              <template #matchedCell="{ data }">
                <span :class="data.data.matched ? 'text-success' : 'text-danger'">
                  {{ data.data.matched ? 'OK' : 'MISSING' }}
                </span>
              </template>
            </DxDataGrid>
          </div>
        </div>
      </div>
    </card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxDataGrid, { DxColumn, DxButton, DxSelection, DxMasterDetail } from "devextreme-vue/data-grid";
import DxPopup from "devextreme-vue/popup";

export default {
  name: "ErpTransferPage",
  components: {
    DxDataGrid,
    DxColumn,
    DxButton,
    DxSelection,
    DxMasterDetail,
    DxPopup,
  },
  data() {
    return {
      activeView: "setup",
      loading: false,
      loadingQueue: false,
      loadingMappings: false,
      savingSettings: false,
      savingMapping: false,
      mappingPopupVisible: false,
      editingMappingId: null,
      settingsForm: {
        transfer_integration_enabled: false,
        iesiri_endpoint: "/datasnap/rest/TServerMethods/IesiriSubunit",
        intrari_endpoint: "/datasnap/rest/TServerMethods/IntraridelaSubunitati",
        default_carnet_iesiri: "IES",
        default_carnet_nir: "NIR",
        warehouse_transfer_endpoint: "/datasnap/rest/TServerMethods/Transferuri",
        default_carnet_transfer: "",
        default_carnet_transfer_nir: "",
        default_carnet_transfer_livr: "",
        warehouse_next_nr_doc: 1,
        retry_max_attempts: 7,
        retry_backoff_base_seconds: 60,
        batch_size: 100,
        timeout_seconds: 90,
        enable_auto_send: false,
        enable_sm_source: true,
        enable_warehouse_source: true,
      },
      mappingForm: {
        mapping_type: "SUBUNITATE",
        source_kind: "INTERNAL_UUID",
        internal_key: "",
        internal_label: "",
        wm_key: "",
        static_value: "",
        priority: 100,
        is_active: true,
        is_fallback: false,
      },
      mappingTypeOptions: [
        "SUBUNITATE",
        "SOURCE_SUBUNITATE",
        "DEST_SUBUNITATE",
        "WAREHOUSE",
        "SOURCE_WAREHOUSE",
        "DEST_WAREHOUSE",
        "PRODUCT",
        "MEASURE_UNIT",
        "COST_CENTER",
      ],
      queueStatusOptions: [
        "NEW",
        "VALIDATED",
        "IESIRI_SENT",
        "CONFIRMED",
        "PARTIAL_ERROR",
        "ERROR",
        "RETRY_WAITING",
        "DEAD",
        "CANCELED",
      ],
      queueFilters: {
        search: "",
        status: "",
      },
      queueAuditByUuid: {},
      queueAuditLoading: {},
      queueAuditErrors: {},
    };
  },
  computed: {
    ...mapGetters({
      queue: "erpIntegration/transferQueue",
      selectedQueueItem: "erpIntegration/transferSelectedItem",
      mappings: "erpIntegration/transferMappings",
      settings: "erpIntegration/transferSettings",
      preview: "erpIntegration/transferPreview",
    }),
    warehouseTransferPayload() {
      return this.buildWarehouseTransferPayload(this.preview, this.selectedQueueItem);
    },
    transferCarnetSymbol() {
      return String(this.settingsForm.default_carnet_transfer || "").trim();
    },
    shouldShowWarehouseStockWarning() {
      return this.preview?.payload?.operation === "WAREHOUSE_TRANSFER" && this.warehouseTransferPayload?.Documente?.some((document) => document?.Operat === "D");
    },
  },
  watch: {
    settings: {
      handler(val) {
        if (val && typeof val === "object") {
          this.settingsForm = { ...this.settingsForm, ...val };
        }
      },
      immediate: true,
    },
    activeView(view) {
      if (view === "queue" && (!this.queue || !this.queue.length)) {
        this.loadQueue();
      } else if (view === "mappings" && (!this.mappings || !this.mappings.length)) {
        this.loadMappings();
      }
    },
  },
  mounted() {
    this.bootstrap();
  },
  methods: {
    ...mapActions({
      fetchSettings: "erpIntegration/fetchTransferSettings",
      updateSettings: "erpIntegration/updateTransferSettings",
      fetchQueue: "erpIntegration/fetchTransferQueue",
      fetchQueueItem: "erpIntegration/fetchTransferQueueItem",
      validateQueueItem: "erpIntegration/validateTransferQueueItem",
      sendQueueItem: "erpIntegration/sendTransferQueueItem",
      retryQueueItem: "erpIntegration/retryTransferQueueItem",
      cancelQueueItem: "erpIntegration/cancelTransferQueueItem",
      bulkRetryQueue: "erpIntegration/bulkRetryTransferQueue",
      previewQueueItem: "erpIntegration/previewTransferQueueItem",
      fetchMappings: "erpIntegration/fetchTransferMappings",
      storeMapping: "erpIntegration/storeTransferMapping",
      updateMapping: "erpIntegration/updateTransferMapping",
      destroyMapping: "erpIntegration/destroyTransferMapping",
    }),
    getTransferDocumentNumber(item) {
      return item?.source_document_number || item?.internal_document_number || "-";
    },
    getTransferSubunit(item, direction) {
      return item?.[`${direction}_department_name`] || item?.[`${direction}_subunit_code`] || "-";
    },
    getTransferAuditJson(event, item, kind) {
      const step = event?.step || (event?.event_type === "TRANSFERURI_SENT" ? "TRANSFERURI" : null);
      const fields = {
        TRANSFERURI: ["payload_transfer_json", "wm_response_transfer_json"],
        IESIRI: ["payload_iesiri_json", "wm_response_iesiri_json"],
        INTRARI: ["payload_intrari_json", "wm_response_intrari_json"],
      };
      const field = fields[step]?.[kind === "request" ? 0 : 1];
      if (!field) {
        return null;
      }

      const context = event?.context_json;
      const snapshot = kind === "request" ? context?.request : context?.response;
      if (snapshot !== null && snapshot !== undefined) {
        return snapshot;
      }
      if (event?.event_type === "SEND_FAILED" && kind === "response") {
        return null;
      }

      return item?.[field] ?? null;
    },
    formatTransferAuditJson(value) {
      if (value === null || value === undefined || value === "") {
        return this.$t("tools.erp_integration.transfer_audit_no_json");
      }

      if (typeof value === "string") {
        try {
          return JSON.stringify(JSON.parse(value), null, 2);
        } catch (error) {
          return value;
        }
      }

      return this.formatJson(value);
    },
    async bootstrap() {
      this.loading = true;
      try {
        await Promise.all([
          this.fetchSettings(),
          this.fetchQueue(this.queueFilters),
          this.fetchMappings(),
        ]);
      } catch (err) {
        console.error("Failed to bootstrap ERP transfer page", err);
      } finally {
        this.loading = false;
      }
    },
    async saveSettings() {
      this.savingSettings = true;
      try {
        await this.updateSettings(this.settingsForm);
        this.$toast?.success(this.$t("tools.erp_integration.settings_saved"));
      } catch (err) {
        this.$toast?.error(err?.response?.data?.message || this.$t("tools.erp_integration.settings_save_failed"));
      } finally {
        this.savingSettings = false;
      }
    },
    async loadQueue() {
      this.loadingQueue = true;
      try {
        await this.fetchQueue(this.queueFilters);
      } finally {
        this.loadingQueue = false;
      }
    },
    onQueueRowExpanding(e) {
      const row = e.component.getVisibleRows().find((visibleRow) => visibleRow.key === e.key);
      if (row?.data) {
        this.loadQueueAudit(row.data);
      }
    },
    async loadQueueAudit(item, force = false) {
      const uuid = item?.uuid;
      if (!uuid || this.queueAuditLoading[uuid] || (!force && this.queueAuditByUuid[uuid])) {
        return;
      }

      this.$set(this.queueAuditLoading, uuid, true);
      this.$delete(this.queueAuditErrors, uuid);
      try {
        const audit = await this.fetchQueueItem({ uuid, setSelected: false });
        if (!audit || !Array.isArray(audit.events)) {
          throw new Error("ERP transfer queue detail did not include audit events.");
        }
        this.$set(this.queueAuditByUuid, uuid, {
          ...audit,
          events: audit.events.map((event) => ({
            ...event,
            request_payload_json: this.getTransferAuditJson(event, audit, "request"),
            response_payload_json: this.getTransferAuditJson(event, audit, "response"),
          })),
        });
      } catch (error) {
        console.error("Failed to load ERP transfer queue audit", error);
        this.$set(
          this.queueAuditErrors,
          uuid,
          error?.response?.data?.message || error?.message || this.$t("tools.erp_integration.audit_load_failed")
        );
      } finally {
        this.$set(this.queueAuditLoading, uuid, false);
      }
    },
    async loadMappings() {
      this.loadingMappings = true;
      try {
        await this.fetchMappings();
      } finally {
        this.loadingMappings = false;
      }
    },
    onQueueSelectionChanged(e) {
      const selected = e.selectedRowsData && e.selectedRowsData[0];
      if (selected) {
        this.fetchQueueItem(selected.uuid);
      }
    },
    onOpenAudit(e) {
      const item = e.row && e.row.data;
      if (item) {
        this.fetchQueueItem(item.uuid).then(() => {
          this.activeView = "audit";
        });
      }
    },
    async onPreviewQueueItem(e) {
      const item = e.row && e.row.data;
      if (item) {
        await this.fetchQueueItem(item.uuid);
        this.loadPreview(item);
        this.activeView = "preview";
      }
    },
    async loadPreview(item) {
      if (!item) return;
      try {
        const codSubunitate = this.resolveCodSubunitate(item, null);
        await this.previewQueueItem({
          source_type: item.source_type || "SM_TRANSFER",
          source_uuid: item.source_uuid,
          cod_subunitate: codSubunitate,
          default_carnet_transfer: this.transferCarnetSymbol,
        });
      } catch (err) {
        console.error("Preview generation failed", err);
      }
    },
    async onValidateQueueItem(e) {
      const item = e.row && e.row.data;
      if (!item) return;
      try {
        await this.validateQueueItem({ uuid: item.uuid, strictMode: true });
        this.$toast?.success(this.$t("tools.erp_integration.validation_finished"));
        this.loadQueue();
      } catch (err) {
        this.$toast?.error(err?.response?.data?.message || this.$t("tools.erp_integration.validation_failed"));
      }
    },
    async onSendQueueItem(e) {
      const item = e.row && e.row.data;
      if (!item) return;
      try {
        const codSubunitate = this.resolveCodSubunitate(item, null);
        const payloadJson = await this.buildSendPayloadJson(item, codSubunitate);

        await this.sendQueueItem({
          uuid: item.uuid,
          force: false,
          cod_subunitate: codSubunitate,
          default_carnet_transfer: this.transferCarnetSymbol,
          payload_json: payloadJson,
        });
        this.$toast?.success(this.$t("tools.erp_integration.send_success"));
        this.loadQueue();
      } catch (err) {
        this.$toast?.error(err?.response?.data?.message || err?.message || this.$t("tools.erp_integration.send_failed"));
        this.loadQueue();
      }
    },
    async onRetryQueueItem(e) {
      const item = e.row && e.row.data;
      if (!item) return;
      try {
        await this.retryQueueItem({
          uuid: item.uuid,
          mode: "IMMEDIATE",
        });
        this.$toast?.success(this.$t("tools.erp_integration.retry_success"));
        this.loadQueue();
      } catch (err) {
        this.$toast?.error(err?.response?.data?.message || err?.message || this.$t("tools.erp_integration.retry_failed"));
        this.loadQueue();
      }
    },
    async onCancelQueueItem(e) {
      const item = e.row && e.row.data;
      if (!item) return;
      const reason = window.prompt(this.$t("tools.erp_integration.cancel_reason_prompt"));
      if (!reason) return;
      try {
        await this.cancelQueueItem({ uuid: item.uuid, reason });
        this.$toast?.success(this.$t("tools.erp_integration.canceled_success"));
        this.loadQueue();
      } catch (err) {
        this.$toast?.error(err?.response?.data?.message || this.$t("tools.erp_integration.cancel_failed"));
      }
    },
    async runBulkRetry() {
      try {
        await this.bulkRetryQueue({
          status_list: ["ERROR", "PARTIAL_ERROR", "RETRY_WAITING"],
          limit: 100,
        });
        this.$toast?.success(this.$t("tools.erp_integration.bulk_retry_success"));
        this.loadQueue();
      } catch (err) {
        this.$toast?.error(err?.response?.data?.message || this.$t("tools.erp_integration.bulk_retry_failed"));
      }
    },
    openMappingPopup(mapping = null) {
      if (mapping) {
        this.editingMappingId = mapping.id;
        this.mappingForm = { ...mapping };
      } else {
        this.editingMappingId = null;
        this.mappingForm = {
          mapping_type: "SUBUNITATE",
          source_kind: "INTERNAL_UUID",
          internal_key: "",
          internal_label: "",
          wm_key: "",
          static_value: "",
          priority: 100,
          is_active: true,
          is_fallback: false,
        };
      }
      this.mappingPopupVisible = true;
    },
    onEditMapping(e) {
      const row = e.row && e.row.data;
      if (row) {
        this.openMappingPopup(row);
      }
    },
    async onDeleteMapping(e) {
      const row = e.row && e.row.data;
      if (!row) return;
      if (window.confirm(this.$t("common.delete_confirm"))) {
        try {
          await this.destroyMapping(row.id);
          this.$toast?.success(this.$t("common.deleted_success"));
          this.loadMappings();
        } catch (err) {
          this.$toast?.error(err?.response?.data?.message || this.$t("tools.erp_integration.delete_failed"));
        }
      }
    },
    async saveMapping() {
      this.savingMapping = true;
      try {
        if (this.editingMappingId) {
          await this.updateMapping({ id: this.editingMappingId, payload: this.mappingForm });
        } else {
          await this.storeMapping(this.mappingForm);
        }
        this.mappingPopupVisible = false;
        this.loadMappings();
        this.$toast?.success(this.$t("common.saved_success"));
      } catch (err) {
        this.$toast?.error(err?.response?.data?.message || this.$t("tools.erp_integration.mapping_save_failed"));
      } finally {
        this.savingMapping = false;
      }
    },
    formatJson(val) {
      try {
        return JSON.stringify(val, null, 2);
      } catch (e) {
        return String(val);
      }
    },
    async buildSendPayloadJson(item, codSubunitate) {
      const preview = await this.previewQueueItem({
        source_type: item.source_type || "SM_TRANSFER",
        source_uuid: item.source_uuid,
        cod_subunitate: codSubunitate,
        default_carnet_transfer: this.transferCarnetSymbol,
      });

      if (preview?.payload?.operation !== "WAREHOUSE_TRANSFER") {
        return null;
      }

      const transfer = this.buildWarehouseTransferPayload(preview, item, codSubunitate);
      if (transfer.CodSubunitate === null || transfer.CodSubunitate === undefined || transfer.CodSubunitate === "") {
        throw new Error(this.$t("tools.erp_integration.transfer_missing_cod_subunitate"));
      }

      return {
        ...preview.payload,
        transfer,
      };
    },
    buildWarehouseTransferPayload(preview, item, resolvedCodSubunitate = null) {
      const payload = preview?.payload?.transfer || {};
      const codSubunitate = resolvedCodSubunitate || this.resolveCodSubunitate(item, preview);
      const transferPayload = this.applyTransferCarnetSymbol(payload);

      if (
        preview?.payload?.operation === "WAREHOUSE_TRANSFER" &&
        codSubunitate !== null &&
        codSubunitate !== undefined &&
        codSubunitate !== "" &&
        (transferPayload.CodSubunitate === null || transferPayload.CodSubunitate === undefined || transferPayload.CodSubunitate === "")
      ) {
        return {
          ...transferPayload,
          CodSubunitate: codSubunitate,
        };
      }

      return transferPayload;
    },
    applyTransferCarnetSymbol(payload) {
      const simbolCarnet = this.transferCarnetSymbol;

      if (!Array.isArray(payload?.Documente)) {
        return payload;
      }

      return {
        ...payload,
        Documente: payload.Documente.map((document) => {
          if (!document) {
            return document;
          }

          return {
            ...document,
            Operat: "D",
            Anulat: "N",
            ...(document.AnLucru !== null && document.AnLucru !== undefined ? { AnLucru: String(document.AnLucru) } : {}),
            ...(document.LunaLucru !== null && document.LunaLucru !== undefined ? { LunaLucru: String(document.LunaLucru) } : {}),
            ...(document.NrDoc !== null && document.NrDoc !== undefined ? { NrDoc: String(document.NrDoc) } : {}),
            ...(Array.isArray(document.Items) ? {
              Items: document.Items.map((item) => {
                if (!item || item.Cant === null || item.Cant === undefined) {
                  return item;
                }

                return {
                  ...item,
                  Cant: String(item.Cant),
                };
              }),
            } : {}),
            ...(simbolCarnet && !document.SimbolCarnet ? { SimbolCarnet: simbolCarnet } : {}),
            ...(document.GestiuneDestinatie !== undefined ? { GestiuneComanda: document.GestiuneDestinatie } : {}),
          };
        }),
      };
    },
    resolveCodSubunitate(item, preview = this.preview) {
      const mappedValue = this.resolveMappedSubunitateValue(item) || this.resolvePreviewSubunitateValue(preview);
      const directValue = item?.cod_subunitate || item?.CodSubunitate;
      const sourceCode = item?.source_subunit_code;
      const destCode = item?.dest_subunit_code;

      if (mappedValue) {
        return mappedValue;
      }

      if (directValue) {
        return directValue;
      }

      if (sourceCode && destCode && String(sourceCode) === String(destCode)) {
        return sourceCode;
      }

      return null;
    },
    resolvePreviewSubunitateValue(preview) {
      const mappingTypes = ["SUBUNITATE", "SOURCE_SUBUNITATE", "DEST_SUBUNITATE"];
      const resolutions = Array.isArray(preview?.mapping_resolutions) ? preview.mapping_resolutions : [];
      const resolution = resolutions.find((entry) => {
        return (
          entry?.matched !== false &&
          mappingTypes.includes(String(entry?.mapping_type || "").toUpperCase()) &&
          entry?.value
        );
      });

      return resolution?.value || null;
    },
    resolveMappedSubunitateValue(item) {
      if (!item || !Array.isArray(this.mappings)) {
        return null;
      }

      const sourceKey = item.source_department_uuid || item.source_subunit_uuid || item.source_subunit_code;
      const destKey = item.dest_department_uuid || item.dest_subunit_uuid || item.dest_subunit_code;
      const lookupKeys = [sourceKey, destKey].filter(Boolean).map((value) => String(value));
      const mappingTypes = ["SUBUNITATE", "SOURCE_SUBUNITATE", "DEST_SUBUNITATE"];
      const mapping = this.mappings.find((entry) => {
        return (
          entry?.is_active !== false &&
          mappingTypes.includes(String(entry?.mapping_type || "").toUpperCase()) &&
          lookupKeys.includes(String(entry?.internal_key || ""))
        );
      });

      return mapping?.wm_key || mapping?.static_value || null;
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case "CONFIRMED":
          return "badge-success";
        case "IESIRI_SENT":
        case "VALIDATED":
          return "badge-info";
        case "PARTIAL_ERROR":
          return "badge-warning";
        case "ERROR":
        case "DEAD":
          return "badge-danger";
        case "RETRY_WAITING":
          return "badge-secondary";
        default:
          return "badge-light";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/components/smagriaTabs.scss";

.transfer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.transfer-header__title {
  margin: 0;
  color: #323232;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
}

.transfer-header__actions {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}

.transfer-header__icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
}

.view-switcher {
  @include smagria-tab-list;
  flex-wrap: wrap;
}

.view-switcher__btn {
  @include smagria-tab-button;
}

.view-panel {
  border: 1px solid #dfe6ef;
  border-radius: 10px;
  background: #fff;
  padding: 14px;
}

.settings-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  border: 0;
  padding: 0;
  margin: 0;
}

.settings-grid--disabled {
  opacity: 0.6;
  pointer-events: none;
}

.col-span-2 {
  grid-column: span 2;
}

.checkbox-row {
  display: flex;
  gap: 20px;
  align-items: center;
  margin: 12px 0;
}

.checkbox-row--master {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.settings-actions {
  margin-top: 20px;
}

.panel-toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.queue-filters {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
  max-width: 600px;
}

.audit-head {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 16px;
  font-size: 13px;
}

.audit-message-cell {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
}

.placeholder-panel {
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px dashed #cbd5e1;
}

.preview-wrap {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.preview-status {
  padding: 12px 16px;
  border-radius: 8px;
}

.preview-status--valid {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #065f46;
}

.preview-status--invalid {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.preview-status__title {
  font-weight: 700;
  font-size: 14px;
}

.preview-status__summary {
  margin-top: 6px;
  white-space: pre-wrap;
  font-size: 13px;
}

.preview-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.audit-event-payload {
  grid-template-columns: minmax(0, 1fr);
  min-width: 0;
}

.audit-event-payload .preview-col {
  min-width: 0;
}

.json-viewer {
  background: #1e293b;
  color: #e2e8f0;
  padding: 14px;
  border-radius: 8px;
  font-family: monospace;
  font-size: 12px;
  max-height: 450px;
  overflow: auto;
}

.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.badge-success { background: #dcfce7; color: #166534; }
.badge-info { background: #e0f2fe; color: #075985; }
.badge-warning { background: #fef9c3; color: #854d0e; }
.badge-danger { background: #fee2e2; color: #991b1b; }
.badge-secondary { background: #f1f5f9; color: #475569; }
.badge-light { background: #f8fafc; color: #64748b; }
.badge-outline-warning { border: 1px solid #ca8a04; color: #ca8a04; }
</style>
